import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../config";
import {
  Row,
  Col,
  Card,
  Typography,
  Spin,
  Alert,
  Input,
  Button,
  Modal,
  message,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import "./Publikasi.scss";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const { Title, Text } = Typography;
const { Search } = Input;

const CACHE_KEY = "publikasi_thumbnails_cache";
const CACHE_DURATION = 5 * 60 * 1000;

const loadCachedThumbnails = () => {
  const raw = localStorage.getItem(CACHE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const now = Date.now();

    if (now - parsed.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed.thumbs || {};
  } catch (e) {
    console.error("Failed to parse cache:", e);
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

const saveThumbnailsToCache = (thumbsMap) => {
  const payload = {
    timestamp: Date.now(),
    thumbs: thumbsMap,
  };
  localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
};

const Publikasi = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  const getPdfThumbnail = async (pdfUrl) => {
    try {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.0 });

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          resolve(url);
        }, "image/png");
      });
    } catch (error) {
      console.error("Gagal generate thumbnail PDF:", pdfUrl, error);
      return null;
    }
  };

  useEffect(() => {
    const fetchPublikasi = async () => {
      try {
        const currentURL = window.location.href;
        const res = await axios.get(
          `${config.BASE_URL}organization/findContentOrganization?namaDomain=${currentURL}`
        );

        const publikasiList = res.data?.data?.publikasi || [];

        if (publikasiList.length === 0) {
          setError("Belum ada publikasi desa cantik.");
          setLoading(false);
          return;
        }

        setData(publikasiList);
        setFiltered(publikasiList);

        // Cek cache thumbnail
        const cachedThumbs = loadCachedThumbnails();
        if (cachedThumbs) {
          setThumbnails(cachedThumbs);
          setLoading(false);
          return;
        }

        // Generate thumbnail PDF
        const thumbPromises = publikasiList.map(async (item) => {
          if (!item?.file) return null;
          const pdfUrl = `${config.BASE_URL_FILE}${item.file}`;
          const thumb = await getPdfThumbnail(pdfUrl);
          return {
            id: item.publikasiDesaCantikId,
            thumb,
          };
        });

        const thumbResults = await Promise.all(thumbPromises);

        const thumbsMap = {};
        thumbResults.forEach((res) => {
          if (res && res.thumb) {
            thumbsMap[res.id] = res.thumb;
          }
        });

        setThumbnails(thumbsMap);
        saveThumbnailsToCache(thumbsMap);
      } catch (err) {
        console.error("Gagal ambil data publikasi:", err);
        setError("Terjadi kesalahan saat mengambil data.");
      } finally {
        setLoading(false);
      }
    };

    fetchPublikasi();
  }, []);

  const onSearch = (value) => {
    setKeyword(value);
    const lower = value.toLowerCase();
    const result = data.filter((item) =>
      item.judul?.toLowerCase().includes(lower)
    );
    setFiltered(result);
  };

  const downloadPdf = (url, judul) => {
    const link = document.createElement("a");
    link.href = url;
    const safeFilename = `${judul}`.replace(/[<>:"/\\|?*]+/g, "").trim();
    link.setAttribute("download", `${safeFilename || "Publikasi"}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const previewPdf = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
      setPdfModalVisible(true);
    } catch (error) {
      message.error("Gagal memuat file PDF");
    }
  };

  return (
    <div className="publikasi-wrapper">
      <Title level={2} className="publikasi-title">
        Publikasi Desa Cantik
      </Title>

      <div className="publikasi-search">
        <Search
          placeholder="Cari berdasarkan judul..."
          allowClear
          enterButton={<Button type="primary">Cari</Button>}
          value={keyword}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="publikasi-loading">
          <Spin
            indicator={<LoadingOutlined className="orange-spin" spin />}
            size="large"
          />
        </div>
      ) : error ? (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ maxWidth: 600, margin: "0 auto" }}
        />
      ) : (
        <Row gutter={[24, 24]}>
          {filtered.length === 0 ? (
            <Col span={24} className="publikasi-empty">
              <Text type="secondary">Tidak ada publikasi ditemukan.</Text>
            </Col>
          ) : (
            filtered.map((item) => {
              const cover = thumbnails[item.publikasiDesaCantikId];
              return (
                <Col key={item.publikasiDesaCantikId} xs={24} sm={12} md={8}>
                  <Card
                    hoverable
                    onClick={() =>
                      previewPdf(`${config.BASE_URL_FILE}${item.file}`)
                    }
                    className="publikasi-card"
                    cover={
                      <div className="card-cover">
                        <img
                          src={cover || "/placeholder-cover.png"}
                          alt="PDF Cover"
                        />
                      </div>
                    }
                  >
                    <Title level={5} style={{ margin: "8px 0" }}>
                      {item.judul || "Judul tidak tersedia"}
                    </Title>
                    <div className="card-desc">
                      {(item.keterangan || "Tidak ada keterangan.").replace(
                        /<[^>]+>/g,
                        ""
                      )}
                    </div>

                    <Button
                      block
                      type="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadPdf(
                          `${config.BASE_URL_FILE}${item.file}`,
                          item.judul || "Publikasi"
                        );
                      }}
                    >
                      Unduh Publikasi
                    </Button>
                  </Card>
                </Col>
              );
            })
          )}
        </Row>
      )}

      <Modal
        title="Preview Publikasi"
        open={pdfModalVisible}
        onCancel={() => {
          setPdfModalVisible(false);
          setPdfBlobUrl(null);
        }}
        footer={null}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ height: "80vh", padding: 0 }}
        destroyOnClose
      >
        {pdfBlobUrl && (
          <iframe
            src={pdfBlobUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="PDF Preview"
          />
        )}
      </Modal>
    </div>
  );
};

export default Publikasi;
